import React from 'react';
import { Link } from 'react-router-dom';
import { CreditCard, Users, Trophy, Music, CircleDot, Info, AlertCircle, ExternalLink, Footprints, Ticket, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { GrasrotandelenBanner } from '../components/GrasrotandelenBanner';
import { useTranslation } from 'react-i18next';
import { SEO } from '../components/SEO';

import i18n from '../i18n';
const t = (k, o) => i18n.t(k, o);

const getmembershipPrices = () => [
  { type: t('pg.Pricing.8c2e46'), price: 350 },
  { type: t('pg.Pricing.8a869e'), price: 250 },
  { type: t('pg.Pricing.8a5a0e'), price: 500, note: t('pg.Pricing.4844cd') },
  { type: t('pg.Pricing.67239d'), price: 200 },
];

const getfootballPrices = () => [
  { type: t('pg.Pricing.ea18ae'), price: 2500 },
  { type: t('pg.Pricing.ec01d2'), price: 1700 },
  { type: t('pg.Pricing.e4ce6f'), price: 1700 },
  { type: t('pg.Pricing.baf93b'), price: 1500 },
  { type: t('pg.Pricing.adb8f3'), price: 1100 },
  { type: t('pg.Pricing.ded220'), price: 500 },
];

const gettennisPrices = () => [
  { type: t('pg.Pricing.075ed3'), price: 400 },
  { type: t('pg.Pricing.41d594'), price: 250 },
];

const getdancePilatesPrices = () => [
  { type: t('pg.Pricing.85d0ab'), price: 1100 },
  { type: t('pg.Pricing.3a3e59'), price: 1400 },
  { type: t('pg.Pricing.bd1f82'), price: 1600 },
  { type: t('pg.Pricing.e14658'), price: 1600 },
  { type: t('pg.Pricing.4c2fe4'), price: 2200 },
];

const getrunClubPrices = () => [
  { type: t('pg.Pricing.8c2e46'), price: 350 },
  { type: t('pg.Pricing.2b2d81'), price: 250 },
  { type: t('pg.Pricing.9667e3'), price: 150 },
];

const PriceTable = ({ prices, icon: Icon, title, footnote }) => (
  <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden">
    <div className="bg-green-500/10 p-4 border-b border-slate-200 dark:border-slate-700">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 bg-green-500/20 rounded-lg flex items-center justify-center">
          <Icon className="w-5 h-5 text-green-500" />
        </div>
        <h3 className="text-xl font-bold text-slate-900 dark:text-white">{title}</h3>
      </div>
    </div>
    <div className="p-4">
      <table className="w-full">
        <tbody>
          {prices.map((item, index) => (
            <tr key={index} className={index !== prices.length - 1 ? 'border-b border-slate-100 dark:border-slate-700' : ''}>
              <td className="py-3">
                <span className="text-slate-600 dark:text-slate-300">{item.type}</span>
                {item.note && (
                  <span className="text-slate-400 text-sm block">{item.note}</span>
                )}
              </td>
              <td className="py-3 text-right whitespace-nowrap pl-3">
                <span className="text-green-500 font-bold text-lg">{item.price} kr</span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {footnote && <p className="text-slate-400 text-sm mt-3">{footnote}</p>}
    </div>
  </div>
);

export const Pricing = () => {
  const { t } = useTranslation();
  
  return (
    <div data-testid="pricing-page">
      <SEO 
        title={t('pg.Pricing.a7260e')} 
        description={t('pg.Pricing.242b48')}
      />
      {/* Hero Section */}
      <section className="relative min-h-[40vh] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1574629810360-7efbbe195018?w=1920&q=80"
            alt={t('pg.Pricing.aa97fe')}
            loading="eager"
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-900 via-slate-900/80 to-transparent" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <p className="text-green-400 font-medium uppercase tracking-widest mb-4">{t('pricing.title', 'Priser')}</p>
          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tighter text-white mb-6">
            {t('pricing.heroTitle1', 'MEDLEMSKAP &')}<br />
            <span className="text-green-400">{t('pricing.heroTitle2', 'TRENINGSAVGIFTER')}</span>
          </h1>
          <p className="text-xl text-white/90 max-w-2xl">
            {t('pricing.heroDescription', 'Oversikt over priser for medlemskap og treningsavgifter i Veitvet Sportsklubb. Gjelder fra 1. januar til 31. desember.')}
          </p>
        </div>
      </section>

      {/* Important Info Banner */}
      <section className="py-6 bg-green-500">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-start gap-4">
            <Info className="w-6 h-6 text-black flex-shrink-0 mt-1" aria-hidden="true" />
            <div>
              <p className="text-black font-bold">{t('pricing.importantInfo', 'Viktig informasjon')}</p>
              <p className="text-black/80 text-sm">
                {t('pricing.importantInfoText', 'Medlemsavgiften er obligatorisk for alle som deltar i faste aktiviteter i klubben, uavhengig av hvilken idrett det gjelder. Medlemskontingenten og treningsavgiften må være betalt for at man skal kunne delta på treninger og kamper.')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Membership Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-slate-50 dark:bg-slate-900">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-green-500 font-medium uppercase tracking-widest mb-4">{t('pricing.annualMembership', 'Årlig medlemskap')}</p>
            <h2 className="text-4xl font-bold tracking-tighter text-slate-900 dark:text-white mb-4">
              {t('pricing.membershipFee', 'MEDLEMSAVGIFT')}
            </h2>
            <p className="text-slate-500 dark:text-slate-400 max-w-2xl mx-auto">
              {t('pg.Pricing.a44612')}
            </p>
          </div>

          <div className="max-w-xl mx-auto">
            <PriceTable 
              prices={getmembershipPrices()} 
              icon={Users} 
              title={t('pg.Pricing.1030d2')} 
            />
          </div>

          <div className="text-center mt-8">
            <a 
              href="https://app.hoopit.io/join/veitvet-sportsklubb-532465/open?skipWelcome=true&push=true"
              target="_blank"
              rel="noopener noreferrer"
            >
              <Button className="bg-green-500 text-white font-bold uppercase tracking-wider px-8 py-6 hover:bg-green-600">
                {t('pg.Pricing.3a845a')}
                <ExternalLink className="ml-2 w-5 h-5" />
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Training Fees Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white dark:bg-slate-800">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-green-500 font-medium uppercase tracking-widest mb-4">{t('pg.Pricing.b851ff')}</p>
            <h2 className="text-4xl font-bold tracking-tighter text-slate-900 dark:text-white mb-4">
              {t('pg.Pricing.818eec')}
            </h2>
            <p className="text-slate-500 dark:text-slate-400 max-w-2xl mx-auto">
              {t('pg.Pricing.06642d')}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Football */}
            <PriceTable 
              prices={getfootballPrices()} 
              icon={CircleDot} 
              title={t('pg.Pricing.10066f')} 
            />

            {/* Tennis */}
            <PriceTable 
              prices={gettennisPrices()} 
              icon={Trophy} 
              title={t('pg.Pricing.0c493d')} 
            />

            {/* Dance & Pilates */}
            <PriceTable 
              prices={getdancePilatesPrices()} 
              icon={Music} 
              title={t('pg.Pricing.022899')} 
              footnote={t('pg.Pricing.04287d')}
            />

            {/* Social Run Club */}
            <PriceTable 
              prices={getrunClubPrices()} 
              icon={Footprints} 
              title={t('pg.Pricing.736bff')} 
            />
          </div>

          {/* Cup/Tournament Notice */}
          <div className="mt-8 bg-slate-100 dark:bg-slate-700/50 rounded-xl border border-slate-200 dark:border-slate-600 p-6">
            <div className="flex items-start gap-4">
              <AlertCircle className="w-6 h-6 text-yellow-500 flex-shrink-0 mt-1" />
              <div>
                <p className="text-slate-900 dark:text-white font-bold mb-2">{t('pg.Pricing.dec6a6')}</p>
                <p className="text-slate-500 dark:text-slate-400">
                  {t('pg.Pricing.e5b0d8')}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Fritidskortet */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white dark:bg-slate-800">
        <div className="max-w-4xl mx-auto">
          <Link
            to="/fritidskortet"
            data-testid="link-fritidskortet"
            className="bg-slate-50 dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-700 hover:border-green-500 p-6 transition-all duration-300 group flex items-center justify-between shadow-sm hover:shadow-md"
          >
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-slate-100 dark:bg-slate-800 group-hover:bg-green-100 dark:group-hover:bg-green-500/20 flex items-center justify-center transition-colors">
                <Ticket className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <p className="text-slate-900 dark:text-white font-bold text-lg">{t('pg.Pricing.c4be9d')}</p>
                <p className="text-slate-500 dark:text-slate-400 text-sm">
                  {t('pg.Pricing.f853ee')}
                </p>
              </div>
            </div>
            <ChevronRight className="w-5 h-5 text-slate-400 group-hover:text-green-600 transition-colors flex-shrink-0" />
          </Link>
        </div>
      </section>

      {/* Cancellation Info */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-slate-50 dark:bg-slate-900">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-8">
            <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-6 flex items-center gap-3">
              <Info className="w-6 h-6 text-green-500" />
              {t('pg.Pricing.054fa4')}
            </h2>
            
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-2">{t('pg.Pricing.1dc124')}</h3>
                <div className="space-y-3 text-slate-500 dark:text-slate-400" data-testid="pricing-withdrawal-text">
                  <p>{t('pg.Pricing.withdraw1')}</p>
                  <p>{t('pg.Pricing.withdraw2')}</p>
                  <p>{t('pg.Pricing.withdraw3')}</p>
                </div>
              </div>

              <div className="border-t border-slate-200 dark:border-slate-700 pt-6">
                <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-2">{t('pg.Pricing.30f771')}</h3>
                <div className="space-y-3 text-slate-500 dark:text-slate-400" data-testid="pricing-payment-text">
                  <p>{t('pg.Pricing.payment1')}</p>
                  <p>{t('pg.Pricing.payment2')}</p>
                  <p>{t('pg.Pricing.payment3')}</p>
                  <p>{t('pg.Pricing.payment4')}</p>
                </div>
              </div>

              <div className="bg-slate-100 dark:bg-slate-700/50 rounded-lg p-4 mt-6">
                <p className="text-slate-600 dark:text-slate-300 text-sm">
                  <strong>{t('pg.Pricing.2a7676')}</strong><br />
                  {t('pg.Pricing.088e48')} <a href="mailto:nickolas.paynter@veitvet-sportsklubb.no" className="text-green-500 hover:underline">nickolas.paynter@veitvet-sportsklubb.no</a>
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white dark:bg-slate-800">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold tracking-tighter text-slate-900 dark:text-white mb-4">
            {t('pg.Pricing.275298')}
          </h2>
          <p className="text-slate-500 dark:text-slate-400 text-lg mb-8">
            {t('pg.Pricing.6d5c0c')}
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a 
              href="https://app.hoopit.io/join/veitvet-sportsklubb-532465/open?skipWelcome=true&push=true"
              target="_blank"
              rel="noopener noreferrer"
            >
              <Button className="bg-green-500 text-white font-bold uppercase tracking-wider px-8 py-6 hover:bg-green-600">
                {t('pg.Pricing.8df769')}
                <ExternalLink className="ml-2 w-5 h-5" />
              </Button>
            </a>
            <a href="/contact">
              <Button variant="outline" className="border-slate-300 dark:border-slate-600 text-slate-900 dark:text-white uppercase tracking-wider px-8 py-6 hover:bg-slate-100 dark:hover:bg-slate-700">
                {t('pg.Pricing.8d1c6b')}
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Grasrotandelen Banner */}
      <GrasrotandelenBanner />
    </div>
  );
};

export default Pricing;
